import { useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  AlertTriangle, Info, Lock, Mail, MoreHorizontal, Phone, Plus, MessageCircle, UserPlus,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { useClient360Contacts, useChangeContactStatus } from "@/hooks/useClients";
import { ContactDrawer } from "@/components/clients/ContactDrawer";
import type {
  Client360Envelope,
  Client360ContactCard,
  Client360ContactMethod,
  ContactStatus,
} from "@/api/clients";

/**
 * Client360 Этап 2 «Контакти» — holding-master tab.
 * Built to VISUAL_Client360_Contacts_v1_2_LOCKED.html in the shared shell + DS
 * tokens. Backend-owned (§0.3): visible methods only, status≠completeness,
 * left_company in its own section (never deleted), allowedActions-gated.
 */

function useHoldingId(): string {
  const { holdingId } = useParams<{ holdingId: string }>();
  return holdingId ?? "";
}

const STATUS_PILL: Record<ContactStatus, string> = {
  active: "bg-muted text-muted-foreground",
  inactive: "bg-muted text-muted-foreground",
  left_company: "bg-muted text-muted-foreground",
  unverified: "bg-warning text-warning-foreground",
};

const PROCESS_LABEL: Record<string, string> = {
  orders: "primary: orders",
  payments: "primary: payments",
  contracts: "підписант",
};

function MethodIcon({ channel }: { channel: Client360ContactMethod["channel"] }) {
  if (channel === "phone") return <Phone className="h-3.5 w-3.5" />;
  if (channel === "email") return <Mail className="h-3.5 w-3.5" />;
  if (channel === "messenger") return <MessageCircle className="h-3.5 w-3.5" />;
  return <Info className="h-3.5 w-3.5" />;
}

function initials(name: string): string {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? "") + (p[1]?.[0] ?? "")).toUpperCase() || "?";
}

export function ClientContactsTab() {
  const id = useHoldingId();
  const navigate = useNavigate();
  const env = useOutletContext<Client360Envelope>();
  const [filter, setFilter] = useState<"all" | "active" | "incomplete">("all");
  const [roleFilter, setRoleFilter] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editContact, setEditContact] = useState<Client360ContactCard | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);

  const q = useClient360Contacts(
    id,
    { role: roleFilter, incomplete: filter === "incomplete" },
    true,
  );
  const statusMut = useChangeContactStatus(id);

  const clientName = env?.header?.displayName || env?.header?.name || "";

  if (q.isPending) {
    return (
      <div className="space-y-2 p-5 md:p-6">
        {[0, 1, 2].map((i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-muted" />)}
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className="p-5 md:p-6">
        <div className="flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4" /> Не вдалося завантажити контакти.
          <button onClick={() => q.refetch()} className="ml-auto font-semibold underline">Повторити</button>
        </div>
      </div>
    );
  }

  const data = q.data!;
  if (data.restricted || data.blockStatus === "forbidden") {
    return (
      <div className="p-5 md:p-6">
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-10 text-center">
          <Lock className="h-5 w-5 text-muted-foreground/50" />
          <span className="text-sm text-muted-foreground">Контакти недоступні (немає доступу).</span>
        </div>
      </div>
    );
  }

  const aa = data.allowedActions;
  const visibleActive = filter === "active" || filter === "incomplete"
    ? data.items
    : data.items;
  const isEmpty = data.items.length === 0 && data.formerItems.length === 0;

  const changeStatus = (contactId: string, status: ContactStatus) => {
    setMenuFor(null);
    statusMut.mutate({ contactId, status });
  };

  return (
    <div className="p-5 md:p-6">
      <p className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Info className="h-3.5 w-3.5" /> Контактні особи холдингу. Показані лише доступні методи звʼязку (privacy backend-owned).
      </p>

      {/* toolbar */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold">
          Контакти{" "}
          <span className="font-normal text-muted-foreground">
            ({data.counts.active} активні · {data.counts.total} всього · холдинг-агрегат)
          </span>
        </h2>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {([["all", "Усі"], ["active", "Активні"], ["incomplete", "Неповні"]] as const).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={cn(
                "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
                filter === k ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="rounded-md border border-border bg-background px-2 py-1 text-xs text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Фільтр за роллю"
          >
            <option value="">Роль ▾</option>
            <option value="decision_maker">ЛПР</option>
            <option value="purchasing">закупівельник</option>
            <option value="accountant">бухгалтер</option>
            <option value="store_manager">керівник точки</option>
            <option value="contract_signatory">підписант</option>
          </select>
          {aa.canCreate && (
            <button
              onClick={() => { setEditContact(null); setDrawerOpen(true); }}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-3.5 w-3.5" /> Додати контакт
            </button>
          )}
        </div>
      </div>

      {isEmpty ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed px-4 py-12 text-center">
          <UserPlus className="h-6 w-6 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            Контактів ще немає. Додайте власника / закупівельника / бухгалтера.
          </p>
          {aa.canCreate && (
            <button
              onClick={() => { setEditContact(null); setDrawerOpen(true); }}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" /> Додати контакт
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2.5">
          {visibleActive.map((c) => (
            <ContactCardRow
              key={c.id}
              c={c}
              navigate={navigate}
              onEdit={() => { setEditContact(c); setDrawerOpen(true); }}
              menuOpen={menuFor === c.id}
              onMenuToggle={() => setMenuFor((m) => (m === c.id ? null : c.id))}
              onChangeStatus={(s) => changeStatus(c.id, s)}
            />
          ))}

          {data.formerItems.length > 0 && (
            <>
              <div className="pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Колишні / неактивні ({data.formerItems.length})
              </div>
              {data.formerItems.map((c) => (
                <ContactCardRow
                  key={c.id}
                  c={c}
                  former
                  navigate={navigate}
                  onEdit={() => { setEditContact(c); setDrawerOpen(true); }}
                  menuOpen={menuFor === c.id}
                  onMenuToggle={() => setMenuFor((m) => (m === c.id ? null : c.id))}
                  onChangeStatus={(s) => changeStatus(c.id, s)}
                />
              ))}
            </>
          )}
        </div>
      )}

      <ContactDrawer
        open={drawerOpen}
        holdingId={id}
        clientName={clientName}
        scopes={data.scopes}
        clientType={data.clientType}
        editContact={editContact}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}

function ContactCardRow({
  c, former, navigate, onEdit, menuOpen, onMenuToggle, onChangeStatus,
}: {
  c: Client360ContactCard;
  former?: boolean;
  navigate: ReturnType<typeof useNavigate>;
  onEdit: () => void;
  menuOpen: boolean;
  onMenuToggle: () => void;
  onChangeStatus: (s: ContactStatus) => void;
}) {
  const statusActions: { label: string; status: ContactStatus }[] =
    c.status === "active" ? [{ label: "Деактивувати", status: "inactive" }, { label: "Позначити «звільнився»", status: "left_company" }]
    : c.status === "unverified" ? [{ label: "Підтвердити", status: "active" }, { label: "Деактивувати", status: "inactive" }]
    : c.status === "inactive" ? [{ label: "Активувати", status: "active" }]
    : [{ label: "Повернути в активні (supervisor)", status: "active" }];

  return (
    <div className={cn("relative flex items-start gap-3 rounded-xl border bg-card p-3.5", former && "opacity-60")}>
      <div className={cn(
        "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold",
        former ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary",
      )}>
        {initials(c.name)}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[15px] font-semibold">{c.name}</span>
          {c.primary.isScopePrimary && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
              основний{c.primary.scopeLabel ? ` · ${c.primary.scopeLabel}` : ""}
            </span>
          )}
          <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", STATUS_PILL[c.status])}>
            {c.statusLabel}
          </span>
          {c.source !== "manual_manager" && (
            <span className="rounded-full bg-info px-2 py-0.5 text-[11px] font-medium text-info-foreground">
              джерело: {c.sourceLabel}
            </span>
          )}
        </div>

        {c.position && <div className="mt-0.5 text-[13px] text-muted-foreground">{c.position}</div>}

        {(c.roles.length > 0 || c.primary.processes.length > 0) && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {c.roles.map((r) => (
              <span key={r.code} className="rounded bg-primary/10 px-2 py-0.5 text-[11.5px] text-primary">{r.label}</span>
            ))}
            {c.primary.processes.map((p) => (
              <span key={p} className="rounded bg-muted px-2 py-0.5 text-[11.5px] text-foreground/70">{PROCESS_LABEL[p] ?? p}</span>
            ))}
          </div>
        )}

        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
          {c.methods.length > 0 ? (
            c.methods.map((m) => (
              <span key={m.id} className="inline-flex items-center gap-1.5">
                <MethodIcon channel={m.channel} /> {m.value}
                {m.isPrimary && <span className="rounded border border-primary/30 px-1 text-[10px] text-primary">осн.</span>}
              </span>
            ))
          ) : (
            <span className="inline-flex items-center gap-1.5 text-warning-foreground">
              <AlertTriangle className="h-3.5 w-3.5" /> метод звʼязку не вказано
            </span>
          )}
        </div>

        {c.assignmentLabels.length > 0 && (
          <div className="mt-2 text-[12px] text-muted-foreground">
            Привʼязка: {c.assignmentLabels.join(" · ")}
            {c.incomplete && <span className="ml-1 text-warning-foreground">· incomplete</span>}
          </div>
        )}
        {c.assignmentLabels.length === 0 && c.incomplete && (
          <div className="mt-2 text-[12px] text-warning-foreground">Неповний контакт (incomplete)</div>
        )}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <button onClick={() => navigate(`/contacts/${c.id}`)} className="text-[12.5px] font-semibold text-primary hover:underline">
          Відкрити
        </button>
        {c.allowedActions.canEdit && (
          <button onClick={onEdit} className="text-[12.5px] font-semibold text-primary hover:underline">
            {c.incomplete ? "Доповнити" : "Редагувати"}
          </button>
        )}
        {c.allowedActions.canChangeStatus && (
          <div className="relative">
            <button onClick={onMenuToggle} aria-label="Ще дії" className="text-muted-foreground hover:text-foreground">
              <MoreHorizontal className="h-4 w-4" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 z-20 mt-1 w-52 rounded-md border bg-popover p-1 shadow-lg">
                {statusActions.map((a) => (
                  <button
                    key={a.status + a.label}
                    onClick={() => onChangeStatus(a.status)}
                    className="block w-full rounded px-2 py-1.5 text-left text-[12.5px] hover:bg-muted"
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
